import { newKeysList } from 'src/components/pageMeta/SEOMetaUtils';
import { transformToCamelCase } from 'src/components/pageMeta/transformToCamelCase';
import { httpService } from 'src/services/fetch/HttpService';
import { TvShowRelatedContent } from 'src/services/GraphQL/Queries/types';
import { LanguageContent } from 'src/services/responseTypes/singleLaunchResponse';
import { DynamicObject } from 'src/utils/commonTypes';
import { GET_ALL_GENRE_URL } from 'src/utils/constants/ApiConstants';
import {
  HTTP_HEADER_KEYS,
  HTTP_METHODS,
  HTTP_URL_PARAMS
} from 'src/utils/constants/HttpMethodsConstants';
import {
  AssetsSubType,
  BrowseItemsType,
  BrowserByTypeResponse,
  DetailsPageSeoConstants,
  TV_SHOW_PAGE_CONSTANTS
} from 'src/utils/constants/TvShowDetailsPageConstants';

import {
  GenreRailFormattedData,
  fetchTvShowGenreData
} from './TvShowDetailsPageService';
import SimpleStore from '../../../store/SimpleStore';

export type GenreLanguageReqObject = {
  asset_subtype: string;
  langRail?: boolean;
  genreRail?: boolean;
};

export enum GenreLangFilterType {
  genre = 'genre',
  language = 'language',
  both = 'both'
}

export type LanguageGenreAPIResponse = {
  languages: [
    {
      id: string;
      value: string;
      image_url: string;
      code: string;
    }
  ];
};

export const getBrowseByUrl = (
  requestObject: GenreLanguageReqObject,
  type: string
) => {
  const { asset_subtype: assetSubType } = requestObject;
  let url;
  const country = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const contentLanguage = SimpleStore.get(HTTP_URL_PARAMS.CONTENT_LANGUAGE);
  if (type === 'language') {
    url = `${GET_ALL_GENRE_URL}?type=${type}&country=${country}&asset_subtype=${assetSubType}`;
    url += `&language=${contentLanguage}`;
  } else {
    url = `${GET_ALL_GENRE_URL}?type=${type}&country=${country}&asset_subtype=${assetSubType}`;
  }
  return url;
};

const getBrowseByGenreRail = async (
  requestObject: GenreLanguageReqObject,
  browseByType: GenreLangFilterType
) => {
  if (!requestObject || !browseByType) {
    return [];
  }
  const url = getBrowseByUrl(requestObject, browseByType);
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const data: LanguageGenreAPIResponse = await httpService.fetchApiData({
    url: url,
    type: HTTP_METHODS.GET,
    headers: headers
  });
  const displayData = [data];

  return displayData.map((item) => {
    const currentkey = Object.keys(item)[0];
    if (currentkey === 'genres') {
      const titleVal = 'Browse by Genre';
      const railName = 'genreRail';
      const rails = {
        title: titleVal,
        items: item[currentkey as keyof object],
        railType: railName
      };
      return rails;
    }
  });
};

const getBrowseByLanguageRail = async (
  requestObject: GenreLanguageReqObject,
  browseByType: GenreLangFilterType
) => {
  if (!requestObject || !browseByType) {
    return [];
  }
  const url = getBrowseByUrl(requestObject, browseByType);
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const data: LanguageGenreAPIResponse = await httpService.fetchApiData({
    url: url,
    type: HTTP_METHODS.GET,
    headers: headers
  });
  const displayData = [data];
  return displayData.map((item) => {
    const currentkey = Object.keys(item)[0];
    if (currentkey === 'languages') {
      const titleVal = 'Browse by Langauge';
      const railName = 'langRail';
      const rails = {
        title: titleVal,
        items: item[currentkey as keyof object],
        railType: railName
      };
      return rails;
    }
  });
};

export const getGenreLanguageRails = async (
  requestObject: GenreLanguageReqObject,
  browseByType: GenreLangFilterType
) => {
  switch (browseByType) {
    case GenreLangFilterType.genre:
      return getBrowseByGenreRail(requestObject, browseByType);
    case GenreLangFilterType.language:
      return getBrowseByLanguageRail(requestObject, browseByType);
    case GenreLangFilterType.both: {
      const [genreArray, languageArray] = await Promise.all([
        getBrowseByGenreRail(requestObject, GenreLangFilterType.genre),
        getBrowseByLanguageRail(requestObject, GenreLangFilterType.language)
      ]);
      return [genreArray, languageArray];
    }

    default:
      return [];
  }
};

export const getTvShowGenreData = async (
  tvShowData: TvShowRelatedContent,
  allSupportedLanguages: LanguageContent[],
  translations: DynamicObject
) => {
  // Response is the tvShowGenre API data to be used for rail

  const contentLanguage = tvShowData?.audioLanguages?.[0] || '';
  const genres =
    tvShowData?.genres?.map((item) => item?.value?.toLowerCase()) || [];
  const headers = [
    HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
    HTTP_HEADER_KEYS.AUTHORIZATION
  ];
  const assetSubType =
    tvShowData?.assetSubType || DetailsPageSeoConstants.TVSHOW;
  const genreRailApiResponse: GenreRailFormattedData[] =
    await fetchTvShowGenreData(
      contentLanguage,
      assetSubType,
      genres,
      allSupportedLanguages,
      { ...translations?.consumption, ...translations?.tvshowdetails },
      headers
    );
  if (genreRailApiResponse && genreRailApiResponse.length) {
    const filteredDataBasedOnGenre = transformToCamelCase(
      genreRailApiResponse,
      newKeysList
    );
    return filteredDataBasedOnGenre;
  }

  return [];
};

export const browseByTypeAPICall = async (
  assetSubType: string,
  browseByType: GenreLangFilterType,
  isAllRequired: boolean
) => {
  const reqObj = {
    asset_subtype:
      assetSubType === AssetsSubType.WEBSERIES
        ? AssetsSubType.ZEE5_ORIGINALS
        : assetSubType
  };

  const data: BrowserByTypeResponse[] = (await getGenreLanguageRails(
    reqObj,
    browseByType
  )) as BrowserByTypeResponse[];

  if (data && data.length) {
    let updatedItems: BrowseItemsType[] = (data[0] && data[0].items) || [];

    // Checking if isAllReq is true
    if (isAllRequired && updatedItems.length) {
      // Adding { id: 'All', value: 'All', image_url: '' } to data[0].items
      const allItem: BrowseItemsType = {
        id: TV_SHOW_PAGE_CONSTANTS.ALL as string,
        value: TV_SHOW_PAGE_CONSTANTS.ALL as string,
        image_url: ''
      };
      updatedItems = [allItem, ...updatedItems];
    }
    return updatedItems;
  }
  return [];
};
